/**
 * How the Stytch session cookie is scoped, shared by every first-party SPA that touches it.
 *
 * **The default is host-only, which is not what this product needs** (#320, found in the #319
 * review). `@stytch/vanilla-js` sets `availableToSubdomains` to `false` unless told otherwise,
 * and even set to `true` it writes `domain = window.location.host` rather than the parent — so
 * a member who signs in at `login[-dev].iamboone.app` was signed out at
 * `my[-dev].iamboone.app`. ADR 0021 decision 3 assumes the opposite ("the Stytch session is a
 * cookie on `.iamboone.app`, so it is present on this host"); this is what makes that true.
 *
 * **It is here rather than in each app because a drift logs people out.** Both SPAs *write*
 * this cookie — the login app on sign-in, and the panel every time the SDK refreshes the
 * five-minute JWT in the background — and the SDK's own comment says why that matters: *"if a
 * developer flips the boolean value of availableToSubdomains at any point, there will be two
 * cookies set which could cause the user to log out, since the js-cookie API always returns
 * the first cookie set."* Two apps holding their own copy of this literal is exactly how the
 * two halves come to disagree, and nothing would fail to build. Same rule as the delivery
 * metric names.
 *
 * **What the scope costs, stated rather than discovered.** Every host in the product sits one
 * label under `iamboone.app` — ADR 0004 decision 1 put the environment on the *service* label
 * (`login-dev.`, not `login.dev.`) so a single-label wildcard certificate covers everything —
 * so this domain reaches **both environments** and the marketing apex, not just one
 * environment's hosts. These cookies are written from JavaScript and therefore cannot be
 * `HttpOnly`, so script on any of those origins can read them.
 *
 * Two things bound that, and neither is an accident:
 *
 * - **The MCP server never reads it.** `toWebRequest` builds its `Headers` from
 *   `event.headers` alone and drops the Function URL's `cookies` array, held by
 *   `tests/invariants/credentials-are-never-ambient.test.ts`. ADR 0021 decision 4's rule is
 *   that a control-layer request carries an *explicit* credential; a cookie arriving at the
 *   API authorises nothing.
 * - **A token is bound to its own Stytch project.** Dev and prod are separate projects with
 *   separate issuers and JWKS, so a dev-issued session is refused by prod's verifier and vice
 *   versa.
 *
 * What is left is exposure rather than acceptance: a prod session token is *readable* by
 * script on a dev origin, in an account ADR 0001 decision 7 keeps deliberately loose. That is
 * a real cost of the single-label host scheme and worth knowing before adding a host to this
 * zone — every new `*.iamboone.app` origin receives this cookie from the day it exists.
 */

/**
 * The registrable domain both environments' hosts sit under, and therefore the only value
 * that reaches a sibling host at all. `login-dev.iamboone.app` and `my-dev.iamboone.app` share
 * no closer ancestor.
 */
const SESSION_COOKIE_DOMAIN = "iamboone.app";

/**
 * Pass this as `createStytchUIClient`'s second argument in **every** first-party SPA. Both
 * fields are required together: `domain` alone does nothing, since the SDK reads it only
 * inside its `availableToSubdomains` branch.
 */
export const STYTCH_CLIENT_OPTIONS = {
  cookieOptions: {
    availableToSubdomains: true,
    domain: SESSION_COOKIE_DOMAIN,
  },
} as const;
